import type { AppRouter } from "@kwp/api/router";
import { createTRPCClient, httpBatchLink, httpLink, splitLink, TRPCClientError } from "@trpc/client";
import { createTRPCContext } from "@trpc/tanstack-react-query";
import superjson from "superjson";
import { presenceHeaders, type PresenceStore } from "./presence";

/**
 * Returns the signed-in user's Firebase ID token, or null when nobody is signed in;
 * `forceRefresh` asks Firebase for a new one even if the cached one looks valid.
 */
export type TokenSource = (forceRefresh?: boolean) => Promise<string | null>;

/** Every api request carries `Authorization: Bearer <Firebase ID token>` when signed in. */
export async function authHeaders(getToken: TokenSource): Promise<Record<string, string>> {
  const token = await getToken();
  return token === null ? {} : { authorization: `Bearer ${token}` };
}

/** The tRPC endpoint under the api's base URL, tolerant of a trailing slash in config. */
export function trpcUrl(apiUrl: string): string {
  return `${apiUrl.replace(/\/+$/, "")}/trpc`;
}

/**
 * `fetch` that answers a 401 once more with a freshly minted token: after a laptop sleeps, the
 * cached ID token can be one the api already refuses (expired, or the clock moved) while
 * Firebase still thinks it is good.
 */
export function refreshingFetch(fetchImpl: typeof fetch, getToken: TokenSource): typeof fetch {
  return async (input, init) => {
    const res = await fetchImpl(input, init);
    if (res.status !== 401) return res;
    const token = await getToken(true);
    if (token === null) return res;
    const headers = new Headers(init?.headers);
    headers.set("authorization", `Bearer ${token}`);
    return fetchImpl(input, { ...init, headers });
  };
}

type BatchFetch = NonNullable<Parameters<typeof httpBatchLink>[0]["fetch"]>;

/**
 * Calls that wait on Gmail or Graph for seconds. A batch answers only when its slowest call
 * does, so these go on their own and the quick calls beside them are not held back.
 */
export const UNBATCHED = new Set(["mail.getThread", "mail.getMessage", "mail.search"]);

export function createApiClient(apiUrl: string, getToken: TokenSource, presence: PresenceStore, fetchImpl: typeof fetch = (input, init) => globalThis.fetch(input, init)) {
  const options = {
    url: trpcUrl(apiUrl),
    fetch: refreshingFetch(fetchImpl, getToken) as BatchFetch,
    transformer: superjson,
    // `x-kwp-client` labels the audit trail for actions (it grants nothing, architecture §9);
    // `x-kwp-presence` is the in-memory passkey proof, sent only while it is valid.
    headers: async () => ({ ...(await authHeaders(getToken)), "x-kwp-client": "web", ...presenceHeaders(presence) }),
  };
  return createTRPCClient<AppRouter>({
    links: [splitLink({ condition: (op) => UNBATCHED.has(op.path), true: httpLink(options), false: httpBatchLink(options) })],
  });
}

export type ApiClient = ReturnType<typeof createApiClient>;

export const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>();

/** The domain error the api attached to a failed call (`code` + `reason`), or null. */
export function domainError(error: unknown): { code: string; reason: string } | null {
  if (!(error instanceof TRPCClientError)) return null;
  const data: unknown = error.data;
  if (typeof data !== "object" || data === null || !("domain" in data)) return null;
  const domain = (data as { domain: unknown }).domain;
  if (typeof domain !== "object" || domain === null) return null;
  const { code, reason } = domain as { code: unknown; reason: unknown };
  return typeof code === "string" && typeof reason === "string" ? { code, reason } : null;
}

/** Failures that the same request will meet again: retrying them only delays the message. */
const FINAL_CODES = new Set(["BAD_REQUEST", "UNAUTHORIZED", "FORBIDDEN", "NOT_FOUND", "PRECONDITION_FAILED", "CONFLICT", "PAYLOAD_TOO_LARGE"]);
export const MAX_RETRIES = 2;

/**
 * The queries' retry rule: a dropped connection, a timeout or a server error (a cold start, a
 * provider hiccup) is tried again up to `MAX_RETRIES` times; an answer the api gave on purpose (a
 * domain error, a refused sign-in, bad input) is not.
 */
export function retryTransient(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES || domainError(error) !== null) return false;
  if (!(error instanceof TRPCClientError)) return true;
  const data: unknown = error.data;
  const code = typeof data === "object" && data !== null && "code" in data ? (data as { code: unknown }).code : undefined;
  return !(typeof code === "string" && FINAL_CODES.has(code));
}

/** A sentence a person can act on, for any failed api call. */
export function errorMessage(error: unknown): string {
  const domain = domainError(error);
  if (domain !== null) return REASONS[domain.reason] ?? `Something went wrong (${domain.reason}).`;
  if (error instanceof Error) return error.message;
  return "Something went wrong.";
}

const REASONS: Record<string, string> = {
  not_invited: "You have not been invited to KitWork+ yet.",
  admin_only: "Only an admin can do that.",
  owner_only: "Only the workspace owner can do that.",
  workspace: "That workspace does not exist or you are not a member.",
  user_not_signed_up: "Nobody with that email has signed in yet. An admin can invite them to this workspace from Settings → Workspaces.",
  personal_is_private: "A Personal workspace cannot have other members.",
  personal_name_fixed: "A Personal workspace keeps its name.",
  name_reserved: "“Personal” is reserved; choose another name.",
  cannot_remove_owner: "The owner cannot be removed.",
  presence_required: "Confirm with your passkey (Touch ID or security key) first.",
  no_passkey: "Add a passkey under Settings → Security first.",
  unknown_passkey: "That passkey is not registered to you.",
  no_challenge: "The passkey prompt timed out. Try again.",
  challenge_expired: "The passkey prompt timed out. Try again.",
  passkey: "That passkey was already removed.",
  approval: "That approval does not exist.",
  approval_decided: "That request was already decided.",
  approval_expired: "That request expired. Ask again from where it came from.",
  not_undoable: "That change cannot be undone.",
  already_undone: "That change was already undone.",
};
